<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import type { Paginator } from '@othercommerce/domain';
  import type { Refund } from '@storefront/resources';

  interface Props {
    refunds: Paginator<Refund>;
  }

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps<Props>();

  const breakpoints = useBreakpoints();
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.REFUNDS') }}
  </AccountMobileNavigation>
  <SfTile v-if="refunds.data.length > 0" class="account-orders" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.REFUNDS') }}
      </h1>
    </template>
    <template #default>
      <div class="account-refunds__wrapper">
        <SfListCard v-for="refund in refunds.data">
          <template #icons>
            <span v-if="refund.customerUnread" class="account-refunds__unread">
              {{ $t('SHOP.ACCOUNT.LABEL.UNREAD_MESSAGES') }}
            </span>
            <SfRefundStatus :status="refund.status" />
          </template>
          <template #default>
            <div class="account-refunds__header">
              <h2 class="account-refunds__heading">
                {{ $t('SHOP.ACCOUNT.HEADER.REFUND_LISTING_NUMBER', { number: refund.number }) }}
              </h2>
              <p class="account-refunds__date">
                <SfDateTime :value="refund.createdAt" />
              </p>
            </div>
          </template>
          <template #actions>
            <SfButton :href="$route('web.account.refund.show', { refund: refund.id })" class="account-refunds__details">
              {{ $t('SHOP.ACTION.SHOW_DETAILS') }}
            </SfButton>
          </template>
        </SfListCard>
      </div>
    </template>
  </SfTile>

  <SfTile v-else>
    <SfDataPlaceholder>
      {{ $t('SHOP.ACCOUNT.MESSAGE.REFUND_PLACEHOLDER') }}
    </SfDataPlaceholder>
  </SfTile>

  <SfPagination :pagination="refunds" />

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-refunds {
    &__wrapper {
      display: flex;
      flex-direction: column;
      row-gap: 1.25rem;
    }

    &__unread {
      font-size: 0.75rem;
      font-weight: var(--font-weight-bold);
    }

    &__header {
      display: flex;
      align-items: center;
      column-gap: 0.625rem;
    }

    &__heading {
      font-size: 1.25rem;
    }

    &__date {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
    }
  }

  @include media.query(lg) {
    .account-refunds {
      row-gap: 1rem;

      &__wrapper .list-card__actions {
        gap: 0.75rem;
      }
    }
  }
</style>
